import {
  EnumTagSemanticoDTOTipo,
  GetTagSemanticiTipoSezione,
  useGetTagSemantici,
  type CampoSemantico,
  type TagSemanticoDTO,
} from '@framework/api-client';
import { type SearchableField } from '@framework/ui';
import { type ColumnsType } from 'antd/es/table';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { PreviewSubmissionImageAttachment } from '../../engine/widgets/upload/components/PreviewSubmissionImageAttachment';
import { type ISemanticField } from './ISemanticField';
import { mapSemanticTagsToSearchableFields } from './mapSemanticTagsToSearchableFields';
import { useSemanticTagSchemaShape } from './useSemanticTagSchemaShape';
import { useSemanticTagSearchableTransform } from './useSemanticTagSearchableTransform';

/**
 * Mappa i tag semantici visibili in colonne della tabella
 */
function getSemanticTagColumns(
  semanticTags: TagSemanticoDTO[],
  sectionType: GetTagSemanticiTipoSezione = GetTagSemanticiTipoSezione.BACKOFFICE,
): ColumnsType<{
  campiSemantici?: CampoSemantico[] | null;
  idRichiesta?: string;
}> {
  return semanticTags.map(tag => {
    const dataIndex =
      sectionType === GetTagSemanticiTipoSezione.FRONTOFFICE
        ? `CAMPO_SEMANTICO.${tag.tipo}.${tag.id}`
        : `CAMPO_SEMANTICO.${tag.id}`;
    return {
      title: tag.titolo ?? tag.nome,
      dataIndex,
      sorter: true,
      key: `semanticTag.${tag.nome}`,
      render: (_, record) => {
        if (!record.campiSemantici) return null;
        const submissionId =
          'idRichiesta' in record && typeof record.idRichiesta === 'string'
            ? record.idRichiesta
            : '';
        const tagType = record.campiSemantici.find(
          t => t.tagSemantico?.id === tag.id,
        )?.tagSemantico?.tipo;
        const tagValue = record.campiSemantici.find(
          t => t.tagSemantico?.id === tag.id,
        )?.valore;

        if (tagType && tagType === EnumTagSemanticoDTOTipo.IMAGE) {
          return (
            <PreviewSubmissionImageAttachment
              submissionId={submissionId}
              attachmentId={tagValue as any}
              borderRadius={4}
              width={32}
              height={32}
            />
          );
        }
        return tagValue;
      },
    };
  });
}

/**
 * Hook che racchiude tutte le logiche per aggiungere le ricerche sui tag semantici
 * alle pagina di lista (procedimenti, richieste, ...), come le colonne extra,
 * i campi di ricerca, il validatore e la trasformazione dei dati.
 */
export function useSemanticTagSearchableExtension(options: {
  /**
   * Se `true`, mostra le colonne con i valori dei campi semantici
   */
  viewSemanticColumns?: boolean;
  /**
   * Filtro utilizzato per filtrare i tag semantici da utilizzare.
   * e.g. Frontoffice sono da vedere solo quelli FO, Backoffice invece entrambi.
   */
  sectionType?: GetTagSemanticiTipoSezione;
  /**
   * Lista di tutti i campi semantici contenuti nelle entità in elenco, utilizzati
   * poi per mostrare i tag semantici nelle tabelle.
   * e.g. procedures.map(s => s.campiSemantici).flat()
   */
  semanticFields: ISemanticField[];
  /**
   * Filtro per tipo di procedimento
   */
  procedureTypeId?: string;
}) {
  // Recupero i tag semantici da BE, filtrandoli per tipoSezione se richiesto
  const semanticTags = useGetTagSemantici({
    ordinamenti: ['nome'],
    tipoSezione: options.sectionType,
    idTipoProcedimento: options.procedureTypeId,
  });

  // Elenco di tutti i tag semantici _ricercabili_
  const searchableSemanticTags = useMemo(() => {
    return semanticTags.data?.dati?.filter(tag => tag.flRicercabile) ?? [];
  }, [semanticTags.data?.dati]);

  // Di tag semantici ricercabili, costruisco i searchable da passare all'advancedSearch
  const searchableFields: SearchableField[] = useMemo(() => {
    return mapSemanticTagsToSearchableFields(searchableSemanticTags);
  }, [searchableSemanticTags]);

  // Costruisco la lista dei tag semantici visualizzabili, prendendoli tra quelli
  // restituiti dalle entità in elenco
  // (e.g. prendo tutti i tag semantici tra i `campiSemantici` dei procedimenti, e di questi
  // filtro solo i visualizzabili).
  const visibleSemanticTags = useMemo(() => {
    const semanticTagList = options.semanticFields.map(s => s.tagSemantico);
    // Rimuovere i tag duplicati
    const semanticTagSet = uniqBy(semanticTagList, tag => tag?.id);
    // Filtrare per flVisibile
    return (
      semanticTags.data?.dati?.filter(tag => {
        return (
          semanticTagSet.findIndex(s => s?.id === tag?.id) !== -1 &&
          tag.flVisualizzabile
        );
      }) ?? []
    );
  }, [options.semanticFields, semanticTags.data?.dati]);

  // Costruisco lo shape dello schema per la ricerca avanzata
  const schemaShape = useSemanticTagSchemaShape({ searchableFields });

  // Costruisco le colonne da visualizzare nella tabella
  const columns = useMemo(() => {
    if (!options.viewSemanticColumns) return [];
    return getSemanticTagColumns(visibleSemanticTags, options.sectionType);
  }, [options.sectionType, options.viewSemanticColumns, visibleSemanticTags]);

  // Funzione di transform necessaria per l'advancedSearch per gestire il passaggio
  // da oggetto (utilizzato internamente) a stringa (utilizzata per la ricerca in query string)
  const transform = useSemanticTagSearchableTransform(searchableSemanticTags);

  return {
    searchableSemanticTags,
    searchableFields,
    visibleSemanticTags,
    schemaShape,
    columns,
    transform,
  };
}
